import { useEffect, useMemo, useState } from "react";
import DOMPurify from "dompurify";
import {
  splitContent,
  contentRequest,
  type CommunityContentBlock,
} from "../../../lib/communityContent";
import { ContentBlock } from "./ContentBlock";
function safeHtml(html: string) {
  const doc = new DOMParser().parseFromString(html, "text/html");
  doc.querySelectorAll("iframe").forEach((frame) => {
    try {
      const url = new URL(frame.src);
      if (
        url.protocol !== "https:" ||
        !["www.youtube.com", "www.youtube-nocookie.com"].includes(
          url.hostname,
        ) ||
        !url.pathname.startsWith("/embed/")
      )
        frame.remove();
    } catch {
      frame.remove();
    }
  });
  return DOMPurify.sanitize(doc.body.innerHTML, {
    ADD_TAGS: ["iframe"],
    ADD_ATTR: ["target", "allow", "allowfullscreen", "frameborder"],
    FORBID_TAGS: ["script"],
  });
}
export function CommunityBody({
  html,
  postId,
  blocks: provided,
  preview = false,
}: {
  html: string;
  postId?: number;
  blocks?: CommunityContentBlock[];
  preview?: boolean;
}) {
  const [blocks, setBlocks] = useState<CommunityContentBlock[]>([]),
    [error, setError] = useState(false),
    [loading, setLoading] = useState(false),
    [retry, setRetry] = useState(0);
  const parts = useMemo(() => splitContent(html), [html]);
  const needs = parts.some((p) => p.type === "block");
  useEffect(() => {
    if (provided || !postId || !needs) return;
    let active = true;
    setLoading(true);
    setError(false);
    setBlocks([]);
    contentRequest<{ blocks: CommunityContentBlock[] }>("resolve", { postId })
      .then((r) => {
        if (active) setBlocks(r.blocks);
      })
      .catch(() => {
        if (active) setError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [postId, provided, needs, retry]);
  return (
    <div className="fe-community-body">
      {error && (
        <div role="alert">
          연결 콘텐츠를 불러오지 못했어요.{" "}
          <button
            className="fe-community-button"
            onClick={() => setRetry((r) => r + 1)}
          >
            다시 시도
          </button>
        </div>
      )}
      {parts.map((p, i) => {
        if (p.type === "text")
          return (
            <div
              key={"text" + i}
              className="prose max-w-none rich-text-content"
              dangerouslySetInnerHTML={{ __html: safeHtml(p.html) }}
            />
          );
        const b = (provided || blocks).find((b) => b.id === p.id);
        return b ? (
          <ContentBlock key={p.id} block={b} preview={preview} />
        ) : (
          <div key={p.id} className="fe-content-block" role="status">
            {loading
              ? "연결 콘텐츠를 불러오고 있어요…"
              : error
                ? "연결 콘텐츠를 확인할 수 없습니다."
                : "연결된 콘텐츠가 없습니다."}
          </div>
        );
      })}
    </div>
  );
}
